import React, { Reducer, useReducer } from 'react';
import {
  GenericActionWithoutPayload,
  GenericActionWithPayload,
} from '../../util';

export enum ZoomFactor {
  WEEKLY = 4,
  DAILY = 2,
}

export const lastWeekOfYear = 26;

export interface CalendarNavigation {
  weekOffset: number;
  zoomFactor: ZoomFactor;
  lastRenderedWeek: number;
  forwardDisabled: boolean;
  backwardDisabled: boolean;
  currentWeek: number;
}

export enum NavigationActionType {
  ZOOM_IN,
  ZOOM_OUT,
  MOVE_BACK,
  MOVE_FORWARD,
  SET_LAST_RENDERED_WEEK,
  SET_CURRENT_WEEK,
  SET_OFFSET,
  RESET,
}

export type NavigationAction =
  | GenericActionWithoutPayload<NavigationActionType.ZOOM_IN>
  | GenericActionWithoutPayload<NavigationActionType.ZOOM_OUT>
  | GenericActionWithoutPayload<NavigationActionType.MOVE_BACK>
  | GenericActionWithoutPayload<NavigationActionType.MOVE_FORWARD>
  | GenericActionWithPayload<
      NavigationActionType.SET_LAST_RENDERED_WEEK,
      number
    >
  | GenericActionWithPayload<NavigationActionType.SET_CURRENT_WEEK, number>
  | GenericActionWithPayload<NavigationActionType.SET_OFFSET, number>
  | GenericActionWithoutPayload<NavigationActionType.RESET>;

// Added as context based on best practice.
export const CalendarNavigationContext = React.createContext<
  React.Dispatch<NavigationAction>
>(() => {});

const getInitState = () => ({
  weekOffset: 0,
  zoomFactor:
    localStorage.getItem('calendarNavigationZoom') === 'DAILY'
      ? ZoomFactor.DAILY
      : ZoomFactor.WEEKLY,
  lastRenderedWeek: 0,
  forwardDisabled: false,
  backwardDisabled: false,
  currentWeek: -1,
});

const reducer: Reducer<CalendarNavigation, NavigationAction> = (
  state,
  action,
) => {
  switch (action.type) {
    case NavigationActionType.ZOOM_IN:
      localStorage.setItem('calendarNavigationZoom', 'DAILY');
      return {
        ...state,
        zoomFactor: ZoomFactor.DAILY,
        currentWeek: -1,
        weekOffset: 0,
      };
    case NavigationActionType.ZOOM_OUT:
      localStorage.removeItem('calendarNavigationZoom');
      return {
        ...state,
        zoomFactor: ZoomFactor.WEEKLY,
        weekOffset: 0,
        currentWeek: -1,
      };
    case NavigationActionType.MOVE_BACK:
      const backLastRenderedWeek = state.lastRenderedWeek - state.zoomFactor;
      return {
        ...state,
        weekOffset: state.weekOffset - state.zoomFactor,
        backwardDisabled: state.weekOffset - state.zoomFactor <= 0,
        forwardDisabled: backLastRenderedWeek === lastWeekOfYear,
        lastRenderedWeek: backLastRenderedWeek,
      };
    case NavigationActionType.MOVE_FORWARD:
      const fwdLastRenderedWeek = state.lastRenderedWeek + state.zoomFactor;
      return {
        ...state,
        weekOffset: state.weekOffset + state.zoomFactor,
        forwardDisabled:
          fwdLastRenderedWeek >= lastWeekOfYear &&
          fwdLastRenderedWeek < lastWeekOfYear + state.zoomFactor,
        backwardDisabled: state.weekOffset < 0,
        lastRenderedWeek: fwdLastRenderedWeek,
      };
    case NavigationActionType.SET_LAST_RENDERED_WEEK:
      return {
        ...state,
        lastRenderedWeek: action.payload,
        backwardDisabled: state.weekOffset <= 0,
        forwardDisabled: action.payload === lastWeekOfYear,
      };
    case NavigationActionType.SET_CURRENT_WEEK:
      return {
        ...state,
        currentWeek: action.payload,
      };
    case NavigationActionType.SET_OFFSET:
      return {
        ...state,
        weekOffset: action.payload,
        backwardDisabled: action.payload <= 0,
      };
    case NavigationActionType.RESET:
      return getInitState();
    default:
      return state;
  }
};

const useCalendarNavigation = (): [
  CalendarNavigation,
  React.Dispatch<NavigationAction>,
] => useReducer(reducer, getInitState());

export default useCalendarNavigation;
